// @ts-check
// Cupons de desconto dos pedidos do site no painel de suporte (contrato 08 de cobrança e operação, seção 7.3.1): criar,
// acompanhar os usos e revogar. O cálculo do exemplo é o mesmo da cotação no Worker (shared/billing/site-coupon-rules.js).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { createSiteCoupon, listSiteCoupons, revokeSiteCoupon } from '../../data/operations.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Skeleton } from '../../ui/feedback.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDate } from '../../lib/format.js';
import { FormError, SelectField, TextField } from '../settings/integration-ui.js';
import { operationsErrorMessage, showOperationsError } from './operations-format.js';
import {
  SITE_COUPON_DISCOUNT_TYPES, SITE_COUPON_DURATIONS, SITE_COUPON_LIMITS, SITE_COUPON_SCOPES, applySiteCoupon, isWellFormedSiteCouponCode,
  normalizeSiteCouponCode,
} from '../../../../shared/billing/site-coupon-rules.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/operations.js').SiteCoupon} SiteCoupon */

const STATUS_COLORS = /** @type {Record<string, string>} */ ({ ACTIVE: 'green', EXHAUSTED: 'blue', EXPIRED: 'gray', REVOKED: 'red' });
// exemplo do formulário: CRM anual sem setup (R$ 625 por mês, R$ 7.500 por ano), o pedido mais comum do site
const EXAMPLE_AMOUNTS = Object.freeze({ monthly: 625, recurring: 7500, setup: 0, importValue: 0, oneTime: 0, today: 7500 });

/** @param {{ onClose: () => void, onCreated: () => void }} props */
function CreateSiteCouponModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: '', label: '', discountType: /** @type {'PERCENT' | 'FIXED'} */ ('PERCENT'), discountValue: '10',
    appliesTo: /** @type {'PLAN' | 'ALL'} */ ('PLAN'), duration: /** @type {'FIRST' | 'ALWAYS'} */ ('FIRST'), validUntil: '', maxUses: '',
  });
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const update = (/** @type {keyof typeof form} */ key) => (/** @type {string} */ value) => setForm((current) => ({ ...current, [key]: value }));
  const code = normalizeSiteCouponCode(form.code);
  const discountValue = Number(form.discountValue);
  const isValid = isWellFormedSiteCouponCode(code) && form.label.trim().length >= SITE_COUPON_LIMITS.minimumLabelLength
    && Number.isInteger(discountValue) && discountValue >= 1 && (form.discountType === 'FIXED' || discountValue <= SITE_COUPON_LIMITS.maximumPercent);
  const example = isValid ? applySiteCoupon(EXAMPLE_AMOUNTS, { discountType: form.discountType, discountValue, appliesTo: form.appliesTo, duration: form.duration }) : null;

  const submit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await createSiteCoupon({
        code, label: form.label.trim(), discountType: form.discountType, discountValue, appliesTo: form.appliesTo, duration: form.duration,
        validUntil: form.validUntil || null, maxUses: form.maxUses ? Number(form.maxUses) : null,
      });
      showSuccessSnackbar(t('operations.admin.siteCoupons.created'));
      onCreated();
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${t('operations.admin.siteCoupons.createTitle')} subtitle=${t('operations.admin.siteCoupons.createSubtitle')}
    confirmLabel=${t('operations.admin.siteCoupons.create')} isConfirmDisabled=${!isValid} isLoading=${isSaving} onClose=${onClose} onConfirm=${submit}>
    <div class="integration-modal-body admin-code-form">
      <div class="admin-code-form-row">
        <${TextField} label=${t('operations.admin.siteCoupons.fields.code')} value=${form.code} onInput=${update('code')} isRequired=${true} autoFocus=${true}
          placeholder=${t('operations.admin.siteCoupons.fields.codePlaceholder')} maxLength=${SITE_COUPON_LIMITS.maximumCodeLength}
          hint=${t('operations.admin.siteCoupons.fields.codeHint')} />
        <${TextField} label=${t('operations.admin.siteCoupons.fields.label')} value=${form.label} onInput=${update('label')} isRequired=${true}
          placeholder=${t('operations.admin.siteCoupons.fields.labelPlaceholder')} maxLength=${SITE_COUPON_LIMITS.maximumLabelLength} />
      </div>
      <div class="admin-code-form-row">
        <${SelectField} label=${t('operations.admin.siteCoupons.fields.discountType')} value=${form.discountType} onChange=${update('discountType')}
          options=${SITE_COUPON_DISCOUNT_TYPES.map((type) => ({ value: type, label: t(`operations.admin.siteCoupons.discountTypes.${type}`) }))} />
        <${TextField} label=${t('operations.admin.siteCoupons.fields.discountValue')} type="number" value=${form.discountValue} onInput=${update('discountValue')} isRequired=${true}
          hint=${t(form.discountType === 'PERCENT' ? 'operations.admin.siteCoupons.fields.percentHint' : 'operations.admin.siteCoupons.fields.fixedHint')} />
      </div>
      <div class="admin-code-form-row">
        <${SelectField} label=${t('operations.admin.siteCoupons.fields.appliesTo')} value=${form.appliesTo} onChange=${update('appliesTo')}
          options=${SITE_COUPON_SCOPES.map((scope) => ({ value: scope, label: t(`operations.admin.siteCoupons.scopes.${scope}`) }))} />
        <${SelectField} label=${t('operations.admin.siteCoupons.fields.duration')} value=${form.duration} onChange=${update('duration')}
          options=${SITE_COUPON_DURATIONS.map((duration) => ({ value: duration, label: t(`operations.admin.siteCoupons.durations.${duration}`) }))} />
      </div>
      <div class="admin-code-form-row">
        <${TextField} label=${t('operations.admin.siteCoupons.fields.validUntil')} type="date" value=${form.validUntil} onInput=${update('validUntil')}
          hint=${t('operations.admin.siteCoupons.fields.validUntilHint')} />
        <${TextField} label=${t('operations.admin.siteCoupons.fields.maxUses')} type="number" value=${form.maxUses} onInput=${update('maxUses')}
          hint=${t('operations.admin.siteCoupons.fields.maxUsesHint')} />
      </div>
      ${example
        ? html`<p class="channel-card-description">${t('operations.admin.siteCoupons.preview', { base: example.subtotal.toLocaleString('pt-BR'), total: example.today.toLocaleString('pt-BR') })}</p>`
        : null}
      <${FormError} message=${error} />
    </div>
  <//>`;
}

/** @param {SiteCoupon} coupon */
function usesText(coupon) {
  return t('operations.admin.siteCoupons.uses', { used: coupon.usedCount, max: coupon.maxUses ?? t('operations.admin.siteCoupons.unlimited') });
}

export function SiteCouponsSection() {
  const [coupons, setCoupons] = useState(/** @type {SiteCoupon[] | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const [revoking, setRevoking] = useState(/** @type {SiteCoupon | null} */ (null));
  const [isRevoking, setIsRevoking] = useState(false);

  const load = () => listSiteCoupons().then((data) => setCoupons(data.coupons)).catch(showOperationsError);
  useEffect(() => {
    load();
  }, []);

  const revoke = async () => {
    if (!revoking) return;
    setIsRevoking(true);
    try {
      await revokeSiteCoupon(revoking.id);
      showSuccessSnackbar(t('operations.admin.siteCoupons.revoked'));
      setRevoking(null);
      load();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsRevoking(false);
    }
  };

  return html`<section class="admin-sign-up-codes admin-site-coupons" aria-labelledby="admin-site-coupons-title">
    <div class="admin-section-header">
      <h2 class="settings-section-title" id="admin-site-coupons-title">${t('operations.admin.siteCoupons.title')}</h2>
      <div class="admin-section-actions">
        <${Button} title=${t('operations.admin.siteCoupons.create')} icon="IconPlus" accent="blue" size="small" disabled=${!coupons} onClick=${() => setIsCreating(true)} />
      </div>
    </div>
    <p class="channel-card-description">${t('operations.admin.siteCoupons.description')}</p>
    ${coupons === null
      ? html`<${Skeleton} height=${96} radius=${8} />`
      : coupons.length === 0
        ? html`<p class="channel-card-description">${t('operations.admin.siteCoupons.empty')}</p>`
        : html`<div class="settings-table admin-code-table">
            <div class="settings-table-row settings-table-header admin-coupon-row">
              <span>${t('operations.admin.siteCoupons.columns.code')}</span>
              <span>${t('operations.admin.siteCoupons.columns.discount')}</span>
              <span>${t('operations.admin.siteCoupons.columns.status')}</span>
              <span class="settings-table-number">${t('operations.admin.siteCoupons.columns.uses')}</span>
              <span>${t('operations.admin.siteCoupons.columns.validUntil')}</span>
              <span></span>
            </div>
            ${coupons.map((coupon) => html`<div key=${coupon.id} class="settings-table-row admin-coupon-row">
              <span class="settings-table-mono">${coupon.code}</span>
              <span class="admin-code-label">
                <span>${coupon.description}</span>
                <span class="settings-table-muted">${[coupon.label, t('operations.admin.siteCoupons.createdBy', { email: coupon.createdByEmail })].join(' · ')}</span>
              </span>
              <span><${Tag} color=${STATUS_COLORS[coupon.status] ?? 'gray'} text=${t(`operations.admin.siteCoupons.statuses.${coupon.status}`)} /></span>
              <span class="settings-table-number">${usesText(coupon)}</span>
              <span class="settings-table-muted">${coupon.validUntil ? formatDate(coupon.validUntil) : t('operations.admin.siteCoupons.noExpiry')}</span>
              <span class="admin-code-actions">${coupon.status === 'ACTIVE'
                ? html`<${Button} title=${t('operations.admin.siteCoupons.revoke')} variant="secondary" accent="danger" size="small" onClick=${() => setRevoking(coupon)} />`
                : null}</span>
            </div>`)}
          </div>`}
    ${isCreating
      ? html`<${CreateSiteCouponModal} onClose=${() => setIsCreating(false)} onCreated=${() => {
          setIsCreating(false);
          load();
        }} />`
      : null}
    ${revoking
      ? html`<${Modal} title=${t('operations.admin.siteCoupons.revokeTitle', { code: revoking.code })} subtitle=${t('operations.admin.siteCoupons.revokeConfirm')}
          confirmLabel=${t('operations.admin.siteCoupons.revoke')} confirmAccent="danger" isLoading=${isRevoking} onClose=${() => setRevoking(null)} onConfirm=${revoke} />`
      : null}
  </section>`;
}
